import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Navigation, MapPin, Truck, Fuel, LogOut, ChevronLeft, Download } from "lucide-react";

/**
 * Mobile-first Quick Input screen for field operators.
 * No sidebar. 4 big-tap actions: Check-in / Check-out / HM-KM / BBM.
 */
export default function QuickInput() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("home"); // home | hmkm | bbm
  const [assets, setAssets] = useState([]);
  const [sites, setSites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(
    () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone
  );

  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data || []));
    api.get("/sites").then((r) => setSites(r.data || []));
    const onPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); toast.success("Aplikasi terpasang di layar utama"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const installPWA = async () => {
    if (!deferredPrompt) return toast.info("Buka di Chrome/Edge (Android/Desktop) untuk install. Untuk iOS: Bagikan → Tambahkan ke Layar Utama.");
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === "accepted") toast.success("Instalasi dimulai...");
  };

  const absen = (type) => {
    if (!navigator.geolocation) return toast.error("Browser tidak dukung GPS");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (p) => {
      try {
        await api.post("/absensi", { lat: p.coords.latitude, lng: p.coords.longitude, type });
        toast.success(type === "check_in" ? "Check-in tercatat" : "Check-out tercatat");
      } catch (err) {
        const q = JSON.parse(localStorage.getItem("absensi_queue") || "[]");
        q.push({ lat: p.coords.latitude, lng: p.coords.longitude, type, queued_at: new Date().toISOString() });
        localStorage.setItem("absensi_queue", JSON.stringify(q));
        toast.warning("Offline — antrian tersimpan");
      } finally { setBusy(false); }
    }, (err) => { toast.error("Gagal ambil lokasi: " + err.message); setBusy(false); },
    { enableHighAccuracy: true, timeout: 10000 });
  };

  const submitHMKM = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/hmkm", {
        asset_id: form.asset_id, date: form.date, hm: Number(form.hm), km: Number(form.km || 0), note: form.note || "",
      });
      toast.success("Log HM/KM tersimpan");
      setForm({}); setMode("home");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitBBM = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/bbm", {
        asset_id: form.asset_id, date: form.date, liter: Number(form.liter),
        price_per_liter: Number(form.price_per_liter), site_id: form.site_id || null, note: form.note || "",
      });
      toast.success("Log BBM tersimpan");
      setForm({}); setMode("home");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); nav("/login"); };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="quick-input-page">
      <header className="border-b border-neutral-800 px-4 py-4 flex items-center gap-3">
        {mode !== "home" ? (
          <button onClick={() => { setMode("home"); setForm({}); }} data-testid="back-to-home" className="text-neutral-400 hover:text-white">
            <ChevronLeft size={20} />
          </button>
        ) : (
          <div className="w-9 h-9 bg-[#FF6600] text-black flex items-center justify-center font-display font-black">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-lg leading-none">Quick Input</div>
          <div className="text-[10px] font-mono uppercase text-neutral-500 mt-1">{user?.name} · {user?.role}</div>
        </div>
        <button onClick={() => nav("/")} data-testid="go-full-app" className="text-xs font-mono uppercase text-[#FF6600] hover:underline">Full App</button>
        <button onClick={doLogout} className="text-neutral-500 hover:text-white"><LogOut size={16} /></button>
      </header>

      <main className="flex-1 p-4">
        {mode === "home" && !installed && (
          <button
            onClick={installPWA}
            data-testid="pwa-install-btn"
            className="w-full mb-4 border border-[#FFD700] bg-[#FFD700]/10 hover:bg-[#FFD700]/20 p-3 flex items-center gap-3 text-left no-transition-all"
            style={{ transition: "background-color 0.15s ease" }}
          >
            <div className="w-10 h-10 bg-[#FFD700] text-black flex items-center justify-center">
              <Download size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-xs uppercase text-[#FFD700]">// PWA</div>
              <div className="font-display font-black text-sm">Pasang ke Layar Utama</div>
              <div className="text-[11px] text-neutral-400 mt-0.5">Akses cepat tanpa buka browser</div>
            </div>
          </button>
        )}
        {mode === "home" && (
          <div className="grid grid-cols-1 gap-3">
            <Button
              data-testid="quick-checkin"
              disabled={busy}
              onClick={() => absen("check_in")}
              className="h-24 rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-display font-black text-2xl flex flex-col gap-1"
            >
              <Navigation size={28} />
              CHECK-IN
            </Button>
            <Button
              data-testid="quick-checkout"
              disabled={busy}
              onClick={() => absen("check_out")}
              className="h-24 rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-display font-black text-2xl flex flex-col gap-1"
            >
              <MapPin size={28} />
              CHECK-OUT
            </Button>
            <Button
              data-testid="quick-hmkm"
              onClick={() => { setForm({ date: today }); setMode("hmkm"); }}
              variant="outline"
              className="h-24 rounded-none border-neutral-800 hover:border-[#FF6600] hover:text-[#FF6600] font-display font-black text-2xl flex flex-col gap-1"
            >
              <Truck size={28} />
              INPUT HM / KM
            </Button>
            <Button
              data-testid="quick-bbm"
              onClick={() => { setForm({ date: today }); setMode("bbm"); }}
              variant="outline"
              className="h-24 rounded-none border-neutral-800 hover:border-[#FFD700] hover:text-[#FFD700] font-display font-black text-2xl flex flex-col gap-1"
            >
              <Fuel size={28} />
              INPUT BBM
            </Button>
          </div>
        )}

        {mode === "hmkm" && (
          <form onSubmit={submitHMKM} className="space-y-4">
            <div className="font-mono text-[10px] uppercase text-[#FF6600]">// INPUT HM/KM</div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-400">Aset</Label>
              <Select value={form.asset_id || ""} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
                <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-14 text-lg" data-testid="hmkm-asset">
                  <SelectValue placeholder="Pilih aset..." />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                  {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-400">Tanggal</Label>
              <Input type="date" required value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-14 text-lg" data-testid="hmkm-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-400">Hour Meter</Label>
                <Input type="number" step="0.1" required value={form.hm ?? ""} onChange={(e) => setForm({ ...form, hm: e.target.value })}
                  className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-14 text-lg" data-testid="hmkm-hm" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-400">Kilometer</Label>
                <Input type="number" step="0.1" value={form.km ?? ""} onChange={(e) => setForm({ ...form, km: e.target.value })}
                  className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-14 text-lg" data-testid="hmkm-km" />
              </div>
            </div>
            <Button type="submit" disabled={busy || !form.asset_id} data-testid="hmkm-submit"
              className="w-full h-14 rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-display font-black text-lg">
              SIMPAN →
            </Button>
          </form>
        )}

        {mode === "bbm" && (
          <form onSubmit={submitBBM} className="space-y-4">
            <div className="font-mono text-[10px] uppercase text-[#FFD700]">// INPUT BBM</div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-400">Aset</Label>
              <Select value={form.asset_id || ""} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
                <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-14 text-lg" data-testid="bbm-asset">
                  <SelectValue placeholder="Pilih aset..." />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                  {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-400">Tanggal</Label>
              <Input type="date" required value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-14 text-lg" data-testid="bbm-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-400">Liter</Label>
                <Input type="number" step="0.1" required value={form.liter ?? ""} onChange={(e) => setForm({ ...form, liter: e.target.value })}
                  className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-14 text-lg" data-testid="bbm-liter" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-400">Harga/L</Label>
                <Input type="number" required value={form.price_per_liter ?? ""} onChange={(e) => setForm({ ...form, price_per_liter: e.target.value })}
                  className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-14 text-lg" data-testid="bbm-price" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-400">Site</Label>
              <Select value={form.site_id || ""} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-14 text-lg" data-testid="bbm-site">
                  <SelectValue placeholder="Pilih site (opsional)..." />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={busy || !form.asset_id} data-testid="bbm-submit"
              className="w-full h-14 rounded-none bg-[#FFD700] hover:bg-[#e6c200] text-black font-display font-black text-lg">
              SIMPAN →
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
